import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User, Plane, Users as UsersIcon, Plus, Minus, Gift, ShieldCheck, Info, Utensils, MapPin, BadgeCheck, CreditCard } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import SEO, { BRAND_LOGO } from "@/components/SEO";
import Celebration from "@/components/Celebration";
import { hasFitGrid, activePlans, firstPricedPlan, rowsForPax, dateAllowed, fmtWindow, tourNights, addDays } from "@/lib/fitPricing";

const input = "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-navy outline-none focus:border-brandorange focus:bg-white";
const label = "mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400";
const MEALS = ["Veg", "Jain", "Non Veg", "Multi Cuisine"];

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function BookingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const settings = useSettings();
  const { verifyOtp } = useAuth();
  const [tour, setTour] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agree, setAgree] = useState(false);
  const [promo, setPromo] = useState("");
  const [celebrate, setCelebrate] = useState(false);
  const [travellers, setTravellers] = useState([{ name: "", type: "Adult", gender: "Male" }, { name: "", type: "Adult", gender: "Male" }]);
  const [p1Edited, setP1Edited] = useState(false);
  const [meal, setMeal] = useState("Veg");
  const [optId, setOptId] = useState("");
  const [plan, setPlan] = useState("");
  const [rowId, setRowId] = useState("");
  const [form, setForm] = useState({ client_name: "", email: "", phone: "", city: "", state: "", pan: "", dob: "", passport_expiry: "", departure_airport: "", travel_date: "", departure_id: "" });
  const [notes, setNotes] = useState("");

  // email verification
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/tours/${slug}`).then((r) => {
      setTour(r.data);
      if (hasFitGrid(r.data)) {
        const o = r.data.hotel_options[0];
        setOptId(o.id);
        setPlan(firstPricedPlan(o));
      }
      setForm((f) => ({
        ...f,
        departure_id: (r.data.package_type === "GROUP" && r.data.departures?.length) ? r.data.departures[0].id : f.departure_id,
        departure_airport: (r.data.departure_airports && r.data.departure_airports.length) ? r.data.departure_airports[0] : "",
      }));
    }).catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => { if (k === "email") { setEmailVerified(false); setOtpSent(false); } setForm({ ...form, [k]: e.target.value }); };
  const setLeadName = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, client_name: v }));
    if (!p1Edited) setTravellers((t) => { const n = [...t]; n[0] = { ...n[0], name: v }; return n; });
  };
  const setTrav = (i, k, v) => { if (i === 0 && k === "name") setP1Edited(true); const t = [...travellers]; t[i] = { ...t[i], [k]: v }; setTravellers(t); };
  const addTrav = () => setTravellers([...travellers, { name: "", type: "Adult", gender: "Male" }]);
  const delTrav = (i) => travellers.length > 2 && setTravellers(travellers.filter((_, x) => x !== i));

  if (notFound) return <div className="flex min-h-[60vh] flex-col items-center justify-center"><h1 className="font-display text-3xl font-bold text-navy">Tour not found</h1><Link to="/tours" className="btn-gradient mt-4 rounded-full px-6 py-3 font-bold">Browse tours</Link></div>;
  if (!tour) return <div className="mx-auto max-w-6xl px-4 py-10"><div className="h-96 w-full skeleton rounded-3xl" /></div>;

  const flagsOn = tour.includes_flags || {};
  const mealsIncluded = flagsOn.meals !== false;
  const isIndia = tour.region === "India";
  const airportOptions = Array.isArray(tour.departure_airports) ? tour.departure_airports.filter(Boolean) : [];

  const grid = hasFitGrid(tour);
  const options = tour.hotel_options || [];
  const selOpt = options.find((o) => o.id === optId) || options[0];
  const plans = grid ? activePlans(selOpt) : [];
  const windows = tour.valid_date_windows || [];
  const nights = tourNights(tour);
  const TRAV_TYPES = grid ? ["Adult", "Child w/ Bed", "Child no Bed", "Infant (0-5)"] : ["Adult", "Child", "Infant"];

  const selectedDep = (tour.departures || []).find((d) => d.id === form.departure_id);
  const base = selectedDep?.price || tour.price;
  const pAdult = tour.price_adult || base;
  const pChild = tour.price_child || Math.round(base * 0.7);
  const pInfant = tour.price_infant || 0;
  const adults = travellers.filter((t) => t.type === "Adult").length;
  const cwb = travellers.filter((t) => t.type === "Child w/ Bed").length;
  const cnb = travellers.filter((t) => t.type === "Child no Bed").length;
  const infants = travellers.filter((t) => (t.type || "").startsWith("Infant")).length;
  const children = grid ? cwb + cnb : travellers.filter((t) => t.type === "Child").length;

  const paxRows = grid ? rowsForPax(selOpt, adults) : [];
  const selRow = paxRows.find((r) => r.id === rowId) || paxRows[0];
  const ppAdult = grid ? Number(selRow?.prices?.[plan] || 0) : 0;

  let amount;
  if (grid) {
    amount = ppAdult * adults + cwb * Number(selOpt?.cwb_price || 0) + cnb * Number(selOpt?.cnb_price || 0);
  } else {
    amount = adults * pAdult + children * pChild + infants * pInfant;
  }
  const advancePct = Math.min(100, Math.max(1, Number(tour.advance_percent) || 100));
  const advanceAmt = Math.round(amount * advancePct / 100);
  const validPax = adults >= 2;
  const gridPriceReady = !grid || (selOpt && plan && selRow && ppAdult > 0);

  const onDate = (e) => {
    const v = e.target.value;
    if (grid && windows.length && !dateAllowed(windows, v, nights)) {
      toast.error(`That date doesn't work — your ${nights}-night stay must finish inside an available travel window. Pick an earlier start date.`);
      setForm((f) => ({ ...f, travel_date: "" }));
      return;
    }
    setForm((f) => ({ ...f, travel_date: v }));
  };
  const selectOption = (id) => { setOptId(id); const o = options.find((x) => x.id === id); setPlan(firstPricedPlan(o)); setRowId(""); };

  const sendOtp = async () => {
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Enter a valid email first"); return; }
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { email: form.email });
      setOtpSent(true);
      setOtpCooldown(30);
      if (data.dev_otp) toast.success(`Verification code (demo): ${data.dev_otp}`, { duration: 9000 });
      else toast.success("Verification code sent to your email");
    } catch (e) { toast.error(apiError(e)); } finally { setOtpLoading(false); }
  };
  const checkOtp = async () => {
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/check", { email: form.email, otp: otpCode });
      setEmailVerified(true);
      if (data.profile) {
        const p = data.profile;
        setForm((f) => ({
          ...f,
          client_name: p.name || f.client_name, phone: p.phone || f.phone,
          city: p.city || f.city, state: p.state || f.state, pan: p.pan || f.pan,
          dob: p.dob || f.dob, passport_expiry: p.passport_expiry || f.passport_expiry,
        }));
        toast.success("Email verified! We pre-filled your saved details.");
      } else {
        toast.success("Email verified!");
      }
    }
    catch (e) { toast.error(apiError(e, "Invalid code")); } finally { setOtpLoading(false); }
  };

  const createBooking = async (payment) => {
    await api.post("/bookings", {
      client_name: form.client_name, phone: form.phone, email: form.email,
      city: form.city, state: form.state, pan: form.pan, dob: form.dob, passport_expiry: form.passport_expiry, email_verified: true,
      tour_slug: tour.slug, tour_title: tour.title, pax: travellers.length,
      adults, children, infants, meal_preference: mealsIncluded ? meal : "",
      hotel_option: grid ? (selOpt?.name || "") : "", meal_plan: grid ? plan : "", vehicle: grid ? (selRow?.vehicle || "") : "",
      travel_date: selectedDep?.date || form.travel_date, amount,
      received_amount: advanceAmt,
      departure_id: form.departure_id, departure_airport: airportOptions.length ? form.departure_airport : "",
      promo_code: promo, notes, travellers,
      razorpay_order_id: payment.order_id, razorpay_payment_id: payment.payment_id, payment_status: "paid",
    });
    // auto-login the traveller with the code they just verified (OTP not yet consumed)
    try { await verifyOtp(form.email, otpCode, form.client_name); } catch (_) { /* ignore */ }
    setCelebrate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.phone || !form.email) { toast.error("Enter lead name, email and phone"); return; }
    if (!form.pan || !form.city || !form.state || !form.dob) { toast.error(`Enter ${isIndia ? "Aadhaar" : "PAN"}, city, state and date of birth`); return; }
    if (!isIndia && !form.passport_expiry) { toast.error("Enter passport expiry date"); return; }
    if (!emailVerified) { toast.error("Please verify your email with OTP"); return; }
    if (travellers.some((t) => !t.name.trim())) { toast.error("Enter every passenger's name"); return; }
    if (!validPax) { toast.error("Minimum booking: 2 Adults"); return; }
    if (!agree) { toast.error("Please accept the Terms & Conditions"); return; }
    if (tour.package_type === "GROUP" && !form.departure_id) { toast.error("Select a departure date"); return; }
    if (grid) {
      if (!form.travel_date) { toast.error("Please select a travel date"); return; }
      if (windows.length && !dateAllowed(windows, form.travel_date, nights)) { toast.error("Selected date is not within the available travel windows"); return; }
      if (!gridPriceReady) { toast.error("Please choose a valid package/vehicle for your number of adults"); return; }
    }
    setSaving(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment gateway. Please retry."); setSaving(false); return; }
      const { data: order } = await api.post("/payments/razorpay/order", { amount: advanceAmt, tour_slug: tour.slug });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: settings?.brand_name || "Happy Holidays Wale",
        description: `${advancePct}% advance — ${tour.title}`,
        image: window.location.origin + BRAND_LOGO,
        prefill: { name: form.client_name, email: form.email, contact: form.phone },
        notes: { tour: tour.title, tour_slug: tour.slug },
        theme: { color: "#002244" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            await createBooking({ order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id });
            toast.success("Payment successful — booking confirmed!");
          } catch (err) { toast.error(apiError(err, "Payment verification failed. Please contact us.")); }
          finally { setSaving(false); }
        },
        modal: { ondismiss: () => { setSaving(false); toast.info("Payment cancelled"); } },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setSaving(false); });
      rzp.open();
    } catch (err) { toast.error(apiError(err)); setSaving(false); }
  };

  return (
    <>
      <SEO title={`Book ${tour.title} | Happy Holidays Wale`} description={`Book the ${tour.title} package.`} image={tour.hero_image} />
      <Celebration open={celebrate} title="Thank you for your booking!" message={`Your ${advancePct}% advance is confirmed. Our travel experts will reach out to finalise the rest of your ${tour.title} journey.`} amount={advanceAmt} onClose={() => nav("/account", { state: { celebrate: true } })} />

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
        <h1 className="font-display text-3xl font-extrabold text-navy sm:text-4xl">{tour.title}</h1>
        <p className="mt-2 text-gray-500">Fill in traveller details, verify your email, review the price and pay your advance to lock the booking.</p>

        <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            {/* Contact */}
            <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
              <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-navy"><User className="h-5 w-5 text-brandorange" /> Contact Information</h2>
              <div><label className={label}>Lead Traveller Name *</label><input data-testid="book-lead-name" className={input} placeholder="Full name" value={form.client_name} onChange={setLeadName} /></div>
              <div className="mt-4">
                <label className={label}>Email (your login) *</label>
                <div className="flex gap-2">
                  <input data-testid="book-email" type="email" className={input} placeholder="you@example.com" value={form.email} onChange={set("email")} disabled={emailVerified} />
                  {emailVerified ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-xl bg-emerald-50 px-4 text-sm font-bold text-emerald-600"><BadgeCheck className="h-4 w-4" /> Verified</span>
                  ) : (
                    <button type="button" onClick={sendOtp} disabled={otpLoading || (otpSent && otpCooldown > 0)} data-testid="verify-email-btn" className="shrink-0 rounded-xl bg-navy px-4 text-sm font-bold text-white disabled:opacity-70">{otpLoading ? "..." : otpSent ? (otpCooldown > 0 ? `Resend ${otpCooldown}s` : "Resend") : "Verify"}</button>
                  )}
                </div>
                {otpSent && !emailVerified && (
                  <div className="mt-2 flex gap-2">
                    <input data-testid="email-otp-input" className={input} placeholder="Enter 6-digit code" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} />
                    <button type="button" onClick={checkOtp} disabled={otpLoading} data-testid="confirm-otp-btn" className="shrink-0 rounded-xl brand-gradient px-4 text-sm font-bold text-white disabled:opacity-70">Confirm</button>
                  </div>
                )}
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><label className={label}>Phone Number *</label><input data-testid="book-phone" className={input} placeholder="Mobile number" value={form.phone} onChange={set("phone")} /></div>
                <div><label className={label}>{isIndia ? "Aadhaar Card Number *" : "PAN Card Number *"}</label><input data-testid="book-pan" className={input} placeholder={isIndia ? "1234 5678 9012" : "ABCDE1234F"} value={form.pan} onChange={(e) => setForm({ ...form, pan: isIndia ? e.target.value : e.target.value.toUpperCase() })} /></div>
                <div><label className={label}>City *</label><input data-testid="book-city" className={input} placeholder="City" value={form.city} onChange={set("city")} /></div>
                <div><label className={label}>State *</label><input data-testid="book-state" className={input} placeholder="State" value={form.state} onChange={set("state")} /></div>
                <div><label className={label}>Date of Birth *</label><input data-testid="book-dob" type="date" className={input} value={form.dob} onChange={set("dob")} /></div>
                {!isIndia && (
                  <div><label className={label}>Passport Expiry *</label><input data-testid="book-passport-expiry" type="date" className={input} value={form.passport_expiry} onChange={set("passport_expiry")} /></div>
                )}
              </div>
            </div>

            {/* Flight & Date */}
            <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
              <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-navy"><Plane className="h-5 w-5 text-brandorange" /> {airportOptions.length ? "Flight & Date Selection" : "Travel Date"}</h2>
              <div className={`grid gap-4 ${airportOptions.length ? "sm:grid-cols-2" : ""}`}>
                {airportOptions.length > 0 && (
                  <div><label className={label}>Departure Airport</label><select data-testid="book-airport" className={input} value={form.departure_airport} onChange={set("departure_airport")}>{airportOptions.map((a) => <option key={a}>{a}</option>)}</select></div>
                )}
                <div><label className={label}>Travel Date *</label>
                  {tour.package_type === "GROUP" ? (
                    <select data-testid="book-departure" className={input} value={form.departure_id} onChange={set("departure_id")}>
                      {(tour.departures || []).map((d) => { const left = d.seats_total - d.seats_booked; return <option key={d.id} value={d.id} disabled={left <= 0}>{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} {left > 0 ? `(${left} left)` : "(Sold Out)"}</option>; })}
                    </select>
                  ) : <input data-testid="book-date" type="date" className={input} value={form.travel_date} onChange={onDate} min={windows.length ? windows.reduce((a, w) => (!a || w.start < a ? w.start : a), "") : undefined} max={windows.length ? windows.reduce((a, w) => { const e = nights > 0 ? addDays(w.end, -(nights - 1)) : w.end; return e > a ? e : a; }, "") : undefined} />}
                  {grid && windows.length > 0 && (
                    <div className="mt-2 rounded-xl bg-navy/5 p-2.5 text-xs text-navy" data-testid="valid-windows">
                      <p className="font-bold">Available travel windows{nights > 0 ? ` — ${nights}N stay must finish inside the window` : ""}:</p>
                      <ul className="mt-1 space-y-0.5">{windows.filter((w) => w.start && w.end).map((w) => { const last = nights > 0 ? addDays(w.end, -(nights - 1)) : w.end; return <li key={w.id}>• {fmtWindow(w)} <span className="text-gray-500">(start by {new Date(last).toLocaleDateString("en-IN", { day: "numeric", month: "short" })})</span></li>; })}</ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Travellers with name + type dropdown */}
            <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><UsersIcon className="h-5 w-5 text-brandorange" /> Passenger Details</h2>
                <button type="button" onClick={addTrav} data-testid="add-traveller" className="flex items-center gap-1 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white"><Plus className="h-3.5 w-3.5" /> Add Passenger</button>
              </div>
              <div className="space-y-3">
                {travellers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex flex-1 gap-2">
                      <input data-testid={`traveller-name-${i}`} className={`${input} flex-[14]`} placeholder={`Passenger ${i + 1} full name`} value={t.name} onChange={(e) => setTrav(i, "name", e.target.value)} />
                      <select data-testid={`traveller-type-${i}`} className={`${input} flex-[4] shrink-0`} value={t.type} onChange={(e) => setTrav(i, "type", e.target.value)}>{TRAV_TYPES.map((tt) => <option key={tt}>{tt}</option>)}</select>
                      <select data-testid={`traveller-gender-${i}`} className={`${input} flex-[3] shrink-0`} value={t.gender || "Male"} onChange={(e) => setTrav(i, "gender", e.target.value)}><option>Male</option><option>Female</option></select>
                    </div>
                    <button type="button" onClick={() => delTrav(i)} data-testid={`remove-traveller-${i}`} disabled={travellers.length <= 2} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-brandred hover:bg-red-50 disabled:opacity-30"><Minus className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className={`mt-3 flex items-start gap-2 rounded-xl p-3 text-xs ${validPax ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid="pax-rule">
                <Info className="h-4 w-4 shrink-0" /> Minimum booking: <b>2 Adults</b>.{!validPax && " Please add at least 2 adults."}
              </div>
            </div>

            {/* FIT: Hotel Option, Meal Plan & Vehicle selection */}
            {grid && (
              <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft" data-testid="fit-package-card">
                <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-navy"><MapPin className="h-5 w-5 text-brandorange" /> Package &amp; Vehicle</h2>
                {options.length > 1 && (
                  <div className="mb-4"><label className={label}>Hotel Option</label>
                    <select data-testid="fit-option" className={input} value={selOpt?.id || ""} onChange={(e) => selectOption(e.target.value)}>{options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
                  </div>
                )}
                {(selOpt?.segments || []).length > 0 && (
                  <div className="mb-4 rounded-xl bg-navy/5 p-3 text-sm text-navy">{selOpt.segments.map((s, i) => <p key={i}>{[s.nights, s.destination, s.hotel_name, s.room_category].filter(Boolean).join(" · ")}</p>)}</div>
                )}
                {plans.length > 0 && (
                  <div className="mb-4"><label className={label}>Meal Plan</label>
                    <div className="flex flex-wrap gap-2">{plans.map((p) => <button key={p} type="button" onClick={() => setPlan(p)} data-testid={`fit-plan-${p}`} className={`rounded-full px-4 py-2 text-sm font-semibold ${plan === p ? "brand-gradient text-white" : "border border-gray-200 text-navy hover:border-brandorange"}`}>{p}</button>)}</div>
                  </div>
                )}
                <div><label className={label}>Vehicle / Price ({adults} Adults)</label>
                  {paxRows.length === 0 ? (
                    <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="no-pax-price">No price is set for {adults} adults in this option. Please adjust the number of adults or contact us on WhatsApp.</div>
                  ) : (
                    <div className="space-y-2">{paxRows.map((r) => { const pp = Number(r.prices?.[plan] || 0); const active = selRow?.id === r.id; return (
                      <button key={r.id} type="button" onClick={() => setRowId(r.id)} data-testid={`fit-vehicle-${r.id}`} className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${active ? "border-brandorange bg-brandorange/5" : "border-gray-200 hover:border-brandorange"}`}>
                        <span className="font-semibold text-navy">{r.vehicle || "Standard"}</span>
                        <span className="font-display font-bold text-navy">₹{pp.toLocaleString("en-IN")} <span className="text-xs font-normal text-gray-400">/ adult</span></span>
                      </button>); })}</div>
                  )}
                </div>
              </div>
            )}

            {/* Meal preference */}
            {mealsIncluded && (
              <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
                <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-navy"><Utensils className="h-5 w-5 text-brandorange" /> Meal Preference</h2>
                <div className="flex flex-wrap gap-2">
                  {MEALS.map((m) => <button key={m} type="button" onClick={() => setMeal(m)} data-testid={`meal-${m.replace(/\s/g, "-").toLowerCase()}`} className={`rounded-full px-4 py-2 text-sm font-semibold ${meal === m ? "brand-gradient text-white" : "border border-gray-200 text-navy hover:border-brandorange"}`}>{m}</button>)}
                </div>
              </div>
            )}

            <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
              <label className={label}>Special Requests / Notes</label>
              <textarea data-testid="book-notes" rows={3} className={input} placeholder="Room preferences, bed options, accessibility needs..." value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            {(tour.payment_policy || tour.cancellation_policy || tour.terms_policy) && (
              <div className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6 text-sm text-gray-600 card-soft">
                {tour.payment_policy && <div><p className="font-display font-bold text-navy">Payment Condition</p><p className="mt-1 whitespace-pre-line">{tour.payment_policy}</p></div>}
                {tour.cancellation_policy && <div><p className="font-display font-bold text-navy">Cancellation &amp; Refund Policy</p><p className="mt-1 whitespace-pre-line">{tour.cancellation_policy}</p></div>}
                {tour.terms_policy && <div><p className="font-display font-bold text-navy">Terms & Conditions</p><p className="mt-1 whitespace-pre-line">{tour.terms_policy}</p></div>}
              </div>
            )}
          </div>

          {/* Price Summary */}
          <aside>
            <div className="sticky top-24 rounded-3xl border border-gray-100 bg-white p-6 card-soft">
              <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-navy">Price Summary</h2><span className="rounded-full bg-navy/5 px-2 py-0.5 text-xs font-bold text-navy">INR</span></div>
              <div className="mt-4 space-y-1 text-sm text-gray-500">
                {grid ? (
                  <>
                    {adults > 0 && <div className="flex justify-between"><span>{adults} × Adult{plan ? ` (${plan})` : ""}</span><span>₹{Number(ppAdult * adults).toLocaleString("en-IN")}</span></div>}
                    {cwb > 0 && <div className="flex justify-between"><span>{cwb} × Child w/ Bed</span><span>₹{Number(cwb * Number(selOpt?.cwb_price || 0)).toLocaleString("en-IN")}</span></div>}
                    {cnb > 0 && <div className="flex justify-between"><span>{cnb} × Child no Bed</span><span>₹{Number(cnb * Number(selOpt?.cnb_price || 0)).toLocaleString("en-IN")}</span></div>}
                    {infants > 0 && <div className="flex justify-between"><span>{infants} × Infant (0-5)</span><span>Free</span></div>}
                    {selRow?.vehicle && <div className="flex justify-between text-xs"><span>Vehicle</span><span className="font-semibold text-navy">{selRow.vehicle}</span></div>}
                  </>
                ) : (
                  <>
                    {adults > 0 && <div className="flex justify-between"><span>{adults} × Adult</span><span>₹{Number(adults * pAdult).toLocaleString("en-IN")}</span></div>}
                    {children > 0 && <div className="flex justify-between"><span>{children} × Child</span><span>₹{Number(children * pChild).toLocaleString("en-IN")}</span></div>}
                    {infants > 0 && <div className="flex justify-between"><span>{infants} × Infant</span><span>{pInfant ? `₹${Number(infants * pInfant).toLocaleString("en-IN")}` : "Free"}</span></div>}
                  </>
                )}
              </div>
              <div className="mt-3 border-t border-gray-100 pt-3">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Total Package Amount</p>
                <p className="font-display text-3xl font-extrabold text-navy" data-testid="booking-total">₹{Number(amount).toLocaleString("en-IN")}</p>
              </div>
              {/* Advance highlight */}
              {advancePct < 100 && (
                <div className="mt-3 flex items-center justify-between rounded-2xl bg-brandorange/10 px-4 py-3" data-testid="advance-box">
                  <div><p className="text-xs font-bold uppercase tracking-wide text-brandorange">{advancePct}% Advance Payment</p><p className="text-xs text-gray-500">Pay now to confirm your seat</p></div>
                  <p className="font-display text-xl font-extrabold text-navy" data-testid="advance-amount">₹{Number(advanceAmt).toLocaleString("en-IN")}</p>
                </div>
              )}

              {tour.promo_enabled && (
                <>
                  <label className={`${label} mt-5`}>Have a Promo Code?</label>
                  <div className="flex gap-2">
                    <input data-testid="promo-code" className={input} placeholder="e.g. FLY50" value={promo} onChange={(e) => setPromo(e.target.value)} />
                    <button type="button" onClick={() => toast.info("Promo codes are validated by our team at confirmation.")} className="rounded-xl bg-navy px-5 text-sm font-bold text-white">Apply</button>
                  </div>
                </>
              )}

              <label className="mt-5 flex items-start gap-2 text-sm text-gray-600"><input data-testid="agree-terms" type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} /><span>I accept the <Link to="/terms" className="font-semibold text-brandorange">Terms and Conditions</Link></span></label>

              <button data-testid="review-continue" disabled={saving} className="btn-gradient mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-5 w-5" /> Pay ₹{Number(advanceAmt).toLocaleString("en-IN")} Now</>}</button>

              {advancePct < 100 && (
                <div className="mt-5 flex gap-3 rounded-2xl bg-amber-50 p-4 text-xs text-amber-800"><Gift className="h-5 w-5 shrink-0 text-brandorange" /><p><b>Hold your seat with just {advancePct}%!</b> Pay ₹{Number(advanceAmt).toLocaleString("en-IN")} now and clear the balance before departure.</p></div>
              )}
              <p className="mt-4 flex items-center justify-center gap-1 text-xs text-gray-400"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Secure booking · {settings?.brand_name}</p>
            </div>
          </aside>
        </form>
      </div>
    </>
  );
}
